import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useEmailVerificationControllerSendMutation,
  useEmailVerificationControllerStatusQuery,
  useEmailVerificationControllerVerifyMutation,
} from '@/services/generated/api';
import { isFetchBaseQueryError } from '@/services/baseApi';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { CenteredCardLayout } from '@/components/ui/centered-card-layout';
import { Input } from '@/components/ui/input';

/**
 * Screen 3 of `docs/DIGITAL_ONBOARDING.md`: confirming the address the account
 * was made with.
 *
 * ## Why a code and not a link
 *
 * A code can be typed on a computer with the mail open on a phone, and it does
 * not depend on a link surviving a corporate mail scanner — those follow every
 * URL they are sent, which consumes a single-use link before its owner ever
 * clicks it.
 *
 * ## Why there is no way past it
 *
 * There used to be — «Lo hago después» — and it was removed on purpose. The
 * address is what the trial clock hangs off and what every notification about
 * the SII's answer is sent to, so an account that never confirms one is an
 * account we cannot reach about the thing it came here to do. The cost the
 * design named is real, and it is accepted: this is a second interruption
 * before anybody has seen anything.
 *
 * Removing the button is not what makes it mandatory. `OnboardingGate` is —
 * every route of the product renders inside it, so there is nowhere else to go.
 */
const CODE_LENGTH = 6;

const TCIT_BRAND = <span className="text-h3 font-extrabold text-fg">TCIT</span>;

const GENERIC_ERROR = 'No pudimos confirmar el código. Inténtalo de nuevo.';

/**
 * The phone code, which is screen 3b. It is skipped past in the same way this
 * one is — `VerifyPhonePage` sends anybody already verified straight on — so
 * pointing here is right whether or not they have done it.
 */
const NEXT = '/verify-phone';

/**
 * The backend's message is written for this screen (it distinguishes expired,
 * too many attempts and simply wrong, which are three different things to do
 * something about), so it is shown as it comes rather than flattened.
 */
function errorMessage(error: unknown): string {
  if (!isFetchBaseQueryError(error)) return GENERIC_ERROR;
  const data = error.data as { message?: unknown } | undefined;
  if (typeof data?.message === 'string') return data.message;
  if (Array.isArray(data?.message) && typeof data.message[0] === 'string') return data.message[0];
  return GENERIC_ERROR;
}

export function VerifyEmailPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { data: status, isLoading } = useEmailVerificationControllerStatusQuery();
  const [verify, { error: verifyError, isLoading: isVerifying }] =
    useEmailVerificationControllerVerifyMutation();
  const [resend, { error: resendError, isLoading: isResending }] =
    useEmailVerificationControllerSendMutation();

  const [code, setCode] = React.useState('');
  // Counted down from what the server said, so the floor cannot drift between
  // the two sides.
  const [resendIn, setResendIn] = React.useState(0);

  React.useEffect(() => {
    if (resendIn <= 0) return;
    const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  // Somebody who already confirmed has nothing to do here — arriving by typing
  // the URL, or coming back after a reload.
  React.useEffect(() => {
    if (status?.verified) navigate(NEXT, { replace: true });
  }, [status, navigate]);

  async function handleVerify(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    const result = await verify({ verifyEmailDto: { code } });
    if (!('error' in result)) navigate(NEXT, { replace: true });
  }

  async function handleResend(): Promise<void> {
    const result = await resend();
    if ('data' in result && result.data) setResendIn(result.data.resendAfterSeconds);
    setCode('');
  }

  const error = verifyError ?? resendError;

  return (
    <CenteredCardLayout
      title="Confirma tu correo"
      description={
        isLoading || !status
          ? 'Cargando…'
          : `Enviamos un código de ${CODE_LENGTH} dígitos a ${status.email}.`
      }
      brand={TCIT_BRAND}
    >
      <form onSubmit={handleVerify} className="flex flex-col gap-stack">
        {error ? (
          <Callout tone="danger" title="No pudimos confirmar tu correo">
            {errorMessage(error)}
          </Callout>
        ) : null}

        <div>
          <label htmlFor="verification-code" className="mb-1 block text-body-s font-medium">
            Código
          </label>
          <Input
            id="verification-code"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={CODE_LENGTH}
            placeholder="492 718"
            // A one-time code is worth the room: it is read off one screen and
            // typed into another, and small digits are what makes that go wrong.
            className="text-center text-h3 tracking-[0.35em]"
          />
        </div>

        <Button type="submit" className="w-full" disabled={code.length !== CODE_LENGTH || isVerifying}>
          {isVerifying ? 'Confirmando…' : 'Confirmar'}
        </Button>

        <p className="text-center text-body-s text-fg-muted">
          {resendIn > 0 ? (
            `Puedes pedir otro código en ${resendIn} s.`
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending}
              className="inline-flex items-center pointer-coarse:min-h-touch font-medium text-action hover:text-action-hover disabled:text-fg-disabled"
            >
              {isResending ? 'Enviando…' : 'Enviarme otro código'}
            </button>
          )}
        </p>
      </form>
    </CenteredCardLayout>
  );
}
